import { getLayer } from '../layer';
import { sound } from '../sound';

/**
 * The particle engine of the themed celebrations: one canvas over the app (inside the FX layer), one
 * requestAnimationFrame loop, frame-rate independent physics (seconds), a hard particle cap (desktop 600,
 * phone 250), devicePixelRatio capped at 2. Set pieces ("actors": a sunflower, a whale, a supernova…) are drawn
 * by code on the same canvas. Everything stops when the tab is hidden and on one tap (honest-play rule 3).
 * The engine knows nothing about games: presets (./presets) describe what to spawn.
 */
export type Draw = (g: CanvasRenderingContext2D, p: Particle) => void;

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** previous position (for the facing of fish, trails) */
  px: number;
  py: number;
  /** age and lifetime, s */
  t: number;
  life: number;
  /** waits this long (s) before it appears and moves */
  delay: number;
  /** px: the radius of the unit shape */
  size: number;
  /** size change per second, relative (0.5 → +50 % / s) */
  grow: number;
  rot: number;
  vr: number;
  /** turn the shape to its direction of motion (fish, sparks) */
  face: boolean;
  /** paper flip: scaleX = cos(t·flip + ph); 0 — none */
  flip: number;
  draw: Draw;
  col: string;
  col2: string;
  /** px/s² downwards; negative — floats up (bubbles, lanterns) */
  gravity: number;
  /** velocity kept per second (1 — no drag, 0.3 — strong air drag, >1 — accelerates: warp streaks) */
  drag: number;
  /** px/s² sideways */
  wind: number;
  /** sideways and vertical sway, px/s, with its frequency (rad/s) */
  sway: number;
  swayY: number;
  swayF: number;
  ph: number;
  alpha: number;
  /** fraction of the life spent fading in / the point (0..1) where fading out starts */
  fadeIn: number;
  fadeOut: number;
  /** additive blending (light: sparks, glows, neon) */
  add: boolean;
  /** free slot for the shape (tile face, wing phase…) */
  data: number;
  /** called when the life ends (not when it leaves the screen): a rocket explodes */
  onEnd: ((p: Particle) => void) | null;
}

export type ParticleInit = Partial<Particle> & { x: number; y: number; draw: Draw };

/** A set piece drawn by code: k = 0..1 of its duration; it may spawn particles. */
export interface Actor {
  dur: number;
  delay?: number;
  /** drawn below the particles (default) or above them */
  front?: boolean;
  draw(g: CanvasRenderingContext2D, k: number, a: ActorFrame): void;
}
export interface ActorFrame {
  /** seconds since the actor started */
  t: number;
  dt: number;
  W: number;
  H: number;
  dpr: number;
}
interface LiveActor extends Actor {
  t: number;
}

const DESKTOP_CAP = 600;
const PHONE_CAP = 250;

let cv: HTMLCanvasElement | null = null;
let g: CanvasRenderingContext2D | null = null;
let dpr = 1;
let W = 0;
let H = 0;
let parts: Particle[] = [];
let actors: LiveActor[] = [];
const timers = new Set<ReturnType<typeof setTimeout>>();
const waiters = new Set<() => void>();
let raf = 0;
let last = 0;
let listening = false;
let capOverride: number | null = null;

/** Phone (coarse pointer or a small screen): 250 particles, otherwise 600. */
export function particleCap(): number {
  if (capOverride !== null) return capOverride;
  let coarse = false;
  try {
    coarse = window.matchMedia('(pointer: coarse)').matches;
  } catch {
    /* no matchMedia */
  }
  return coarse || Math.min(innerWidth, innerHeight) < 600 ? PHONE_CAP : DESKTOP_CAP;
}

/** Tests / the gallery: force a cap (null — back to the device rule). */
export function setParticleCap(n: number | null): void {
  capOverride = n;
}

/** Scale a desktop particle count to the cap of this device. */
export function budget(n: number): number {
  return Math.max(1, Math.round((n * particleCap()) / DESKTOP_CAP));
}

export function viewport(): { W: number; H: number } {
  return { W: innerWidth, H: innerHeight };
}

// ---- random (seedable for the gallery) ---------------------------------------------------------------

let rnd: () => number = Math.random;
/** A fixed seed makes every preset draw the same frames (the gallery screenshots); null — Math.random. */
export function seedCelebrations(seed: number | null): void {
  if (seed === null) {
    rnd = Math.random;
    return;
  }
  let a = seed >>> 0;
  rnd = () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
export const rand = (): number => rnd();
export const between = (a: number, b: number): number => a + (b - a) * rnd();
export function pick<T>(a: readonly T[]): T {
  return a[Math.floor(rnd() * a.length) % a.length] as T;
}

// ---- canvas ------------------------------------------------------------------------------------------

function fit(): void {
  if (!cv) return;
  dpr = Math.min(2, window.devicePixelRatio || 1);
  W = innerWidth;
  H = innerHeight;
  cv.width = Math.round(W * dpr);
  cv.height = Math.round(H * dpr);
}

function ensureCanvas(): CanvasRenderingContext2D | null {
  if (cv && cv.isConnected && g) return g;
  cv = document.createElement('canvas');
  cv.dataset.fxCelebration = '';
  cv.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;';
  getLayer().appendChild(cv);
  fit();
  g = cv.getContext('2d');
  return g;
}

// ---- skip / visibility --------------------------------------------------------------------------------

const onPointer = (): void => skipCelebrations();
const onKey = (e: KeyboardEvent): void => {
  if (e.key === 'Escape' || e.key === ' ' || e.key === 'Enter') skipCelebrations();
};
const onHidden = (): void => {
  if (document.visibilityState === 'hidden') cutCelebrations();
};

function listen(on: boolean): void {
  if (on === listening) return;
  listening = on;
  const m = on ? 'addEventListener' : 'removeEventListener';
  window[m]('pointerdown', onPointer, { capture: true });
  window[m]('keydown', onKey as EventListener, { capture: true });
  window[m]('resize', fit);
  document[m]('visibilitychange', onHidden);
}

// ---- spawning -----------------------------------------------------------------------------------------

/** Adds a particle unless the cap is reached (then null). */
export function spawn(o: ParticleInit): Particle | null {
  if (parts.length >= particleCap()) return null;
  const p: Particle = {
    vx: 0,
    vy: 0,
    t: 0,
    life: 1,
    delay: 0,
    size: 4,
    grow: 0,
    rot: 0,
    vr: 0,
    face: false,
    flip: 0,
    col: '#FFD166',
    col2: '#FFFFFF',
    gravity: 0,
    drag: 1,
    wind: 0,
    sway: 0,
    swayY: 0,
    swayF: 3,
    ph: rnd() * Math.PI * 2,
    alpha: 1,
    fadeIn: 0,
    fadeOut: 0.7,
    add: false,
    data: 0,
    onEnd: null,
    ...o,
    px: o.x,
    py: o.y,
  };
  if (p.face && (p.vx || p.vy)) p.rot = Math.atan2(p.vy, p.vx);
  parts.push(p);
  start();
  return p;
}

export function addActor(a: Actor): void {
  actors.push({ ...a, t: -(a.delay ?? 0) });
  start();
}

/** A timer owned by the engine: cancelled on a skip / hidden tab. */
export function after(ms: number, fn: () => void): void {
  const t = setTimeout(() => {
    timers.delete(t);
    fn();
  }, ms);
  timers.add(t);
  start();
}

/** Resolves after `ms` or at once on a skip. */
export function hold(ms: number): Promise<void> {
  return new Promise<void>((res) => {
    const done = () => {
      waiters.delete(done);
      res();
    };
    waiters.add(done);
    after(ms, done);
  });
}

// ---- the loop -----------------------------------------------------------------------------------------

function start(): void {
  listen(true);
  if (raf) return;
  if (!ensureCanvas()) {
    parts = [];
    actors = [];
    return;
  }
  last = performance.now();
  raf = requestAnimationFrame(frame);
}

function stop(): void {
  if (raf > 0) cancelAnimationFrame(raf);
  raf = 0;
  if (g && cv) {
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, cv.width, cv.height);
  }
  if (!timers.size) listen(false);
}

function drawActors(c: CanvasRenderingContext2D, dt: number, front: boolean): void {
  for (let i = actors.length - 1; i >= 0; i--) {
    const a = actors[i];
    if (!a || !!a.front !== front) continue;
    if (a.t < 0) continue;
    const k = Math.min(1, a.t / a.dur);
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    c.globalAlpha = 1;
    c.globalCompositeOperation = 'source-over';
    try {
      a.draw(c, k, { t: a.t, dt, W, H, dpr });
    } catch {
      // a broken set piece is dropped, the rest of the celebration goes on
      a.t = a.dur;
    }
  }
}

function frame(now: number): void {
  // a frame in progress: spawns from set pieces must not schedule a second loop
  raf = -1;
  const c = g;
  if (!c || !cv) return;
  const dt = Math.min(0.05, Math.max(0, (now - last) / 1000));
  last = now;
  c.setTransform(1, 0, 0, 1, 0, 0);
  c.clearRect(0, 0, cv.width, cv.height);

  for (const a of actors) a.t += dt;
  drawActors(c, dt, false);

  for (let i = parts.length - 1; i >= 0; i--) {
    const p = parts[i];
    if (!p) continue;
    if (p.delay > 0) {
      p.delay -= dt;
      continue;
    }
    p.t += dt;
    const gone =
      p.t >= p.life ||
      (p.y > H + 120 && p.vy >= 0 && p.gravity >= 0) ||
      (p.y < -160 && p.vy <= 0 && p.gravity <= 0) ||
      p.x < -220 ||
      p.x > W + 220;
    if (gone) {
      const last = parts.pop();
      if (last && last !== p) parts[i] = last;
      if (p.t >= p.life) p.onEnd?.(p);
      continue;
    }
    const keep = p.drag === 1 ? 1 : Math.pow(p.drag, dt);
    p.vx = p.vx * keep + p.wind * dt;
    p.vy = p.vy * keep + p.gravity * dt;
    p.px = p.x;
    p.py = p.y;
    const sw = Math.sin(p.t * p.swayF + p.ph);
    p.x += (p.vx + sw * p.sway) * dt;
    p.y += (p.vy + Math.cos(p.t * p.swayF * 0.8 + p.ph) * p.swayY) * dt;
    if (!p.face) p.rot += p.vr * dt;

    const k = p.t / p.life;
    let a = p.alpha;
    if (p.fadeIn > 0 && k < p.fadeIn) a *= k / p.fadeIn;
    if (k > p.fadeOut) a *= Math.max(0, 1 - (k - p.fadeOut) / (1 - p.fadeOut));
    if (a <= 0.004) continue;
    const s = p.size * Math.max(0, 1 + p.grow * p.t);
    if (s <= 0.05) continue;
    // facing follows the motion; a frame without movement (dt = 0) keeps the last direction
    if (p.face && Math.abs(p.x - p.px) + Math.abs(p.y - p.py) > 0.001)
      p.rot = Math.atan2(p.y - p.py, p.x - p.px);
    const rot = p.rot;
    const fx = p.flip ? Math.cos(p.t * p.flip + p.ph) : 1;
    const co = Math.cos(rot);
    const si = Math.sin(rot);
    const sx = s * fx * dpr;
    const sy = s * dpr;
    c.globalAlpha = Math.min(1, a);
    c.globalCompositeOperation = p.add ? 'lighter' : 'source-over';
    c.setTransform(co * sx, si * sx, -si * sy, co * sy, p.x * dpr, p.y * dpr);
    try {
      p.draw(c, p);
    } catch {
      p.life = 0;
    }
  }

  drawActors(c, dt, true);
  actors = actors.filter((a) => a.t < a.dur);
  c.globalAlpha = 1;
  c.globalCompositeOperation = 'source-over';

  if (parts.length || actors.length || timers.size) raf = requestAnimationFrame(frame);
  else stop();
}

// ---- stopping -----------------------------------------------------------------------------------------

const cutHooks = new Set<() => void>();
/** DOM pieces of a celebration (the reduced-motion glow, the mega amount) register their removal here. */
export function onCut(fn: () => void): () => void {
  cutHooks.add(fn);
  return () => cutHooks.delete(fn);
}

/** Stops every particle, set piece and pending spawn at once (a skip, a hidden tab, leaving the screen). */
export function cutCelebrations(): void {
  for (const t of timers) clearTimeout(t);
  timers.clear();
  parts = [];
  actors = [];
  for (const fn of Array.from(cutHooks)) fn();
  cutHooks.clear();
  for (const w of Array.from(waiters)) w();
  waiters.clear();
  stop();
  listen(false);
}

/** One tap / Esc / Space / Enter: the celebration and its ringing sound stop together. */
export function skipCelebrations(): void {
  if (!busy()) return;
  cutCelebrations();
  sound.cutCelebration();
}

/** Something is still celebrating (particles, set pieces, scheduled bursts or DOM pieces). */
export function busy(): boolean {
  return parts.length > 0 || actors.length > 0 || timers.size > 0 || cutHooks.size > 0;
}

export function liveParticles(): number {
  return parts.length;
}
export function debugParticles(): Particle[] {
  return parts;
}
export function liveActors(): number {
  return actors.length;
}
